import { Button } from "components/DesignSystem/Button/Button";
import { LinkBankAccount } from "components/LinkBankAccount";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { BankConnection } from "types/Models/banks";
import { memo, useCallback, useEffect, useState } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { ArrowRight } from "components/icons/ArrowRight";
import { useBankConnect } from "hooks/useBankConnect";
import {
  useFinishMigrationMutation,
  useGetIntermediateBanksQuery,
  AccountMigrateMap,
} from "store/apis/bankConnections";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { Field, FieldProps, useFormikContext } from "formik";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { stopPropagation } from "utils/stopPropagation";
import { FormikForm } from "components/FormikForm/FormikForm";
import Loader from "components/DesignSystem/Loader/loader";
import { CheckCircleFilled } from "components/icons/CheckCircleFilled";
import { useModal } from "hooks/useModal";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { ArrowLightLine } from "components/icons/ArrowLightLine";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { MultiValue, SingleValue } from "react-select";
import { ConditionalLink } from "components/conditionalLink";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { CaretDown } from "components/icons/CaretDown";
import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";
import { ReviewAccountsModal } from "./ReviewModal";
import { pluralize } from "utils/pluralize";
import { produce } from "immer";
import { TakeConfirmationModal } from "components/TakeConfirmationModal/TakeConfirmationModal";
import { PlaidToDirectMigration } from "components/PlaidToDirectMigration/PlaidToDirectMigration";

const isMapComplete = (
  values: MigrationFormValues,
  connection: BankConnection,
  newAccounts: {
    name: string;
    mask: string;
  }[]
) => {
  return (
    values.account_mappers.filter(Boolean).length >= newAccounts.length &&
    connection.bank_accounts?.every((account) => {
      const accountMapper = values.account_mappers.find(
        (a) => a?.account_id === account.uuid
      );

      return (
        accountMapper &&
        (accountMapper.account_id ||
          accountMapper.action === "keep_active" ||
          (accountMapper.action === "closed" && accountMapper.closed_on))
      );
    })
  );
};

const MemoizedLinkBankAccount = memo(LinkBankAccount);

type MigrationStep = 1 | 2 | 3;

type MigrationFormValues = {
  txn_pull_choice: string;
  txn_pull_date: string;
  account_mappers: (
    | {
        account_id?: string;
        name?: string;
        mask?: string;
        action?: "closed" | "keep_active";
        closed_on?: string;
        closing_balance?: number;
      }
    | undefined
  )[];
};

const MigrationStepper = ({
  currentStep,
  onStepperClick,
}: {
  onStepperClick: (step: number) => void;
  currentStep: number;
}) => {
  const { isMobile } = useWindowSize();

  const breadcrumbs = [
    { name: "Connect", step: 1 },
    { name: "Map accounts", step: 2 },
    { name: "Manage past transactions", step: 3 },
  ];

  return (
    <div className="t-pt-2">
      <Stepper
        size={isMobile ? "xs" : "small"}
        direction="horizontal"
        className="sm:t-gap-y-1"
      >
        {breadcrumbs?.map(({ name, step }) => (
          <Stepper.Step
            step={step}
            key={step}
            isActive={currentStep >= step}
            clickable={currentStep >= step}
            onClick={() => onStepperClick(step)}
          >
            <div className="t-flex t-items-center">
              {name}
              {step !== breadcrumbs.length && (
                <ArrowRight color="currentColor" />
              )}
            </div>
          </Stepper.Step>
        ))}
      </Stepper>
    </div>
  );
};

const MapAccounts = ({
  connection,
  intermediateBanks,
  isLoading,
}: {
  connection: BankConnection;
  intermediateBanks?: {
    name: string;
    mask: string;
  }[];
  isLoading: boolean;
}) => {
  const { setFieldValue, values } = useFormikContext<MigrationFormValues>();

  const mappedAccounts = values.account_mappers
    .filter((a) => a?.action !== "closed" && a?.action !== "keep_active")
    .map((a) => a?.account_id);

  const allNewAccountsMapped = intermediateBanks?.every((account, index) =>
    Boolean(values.account_mappers[index])
  );

  const unmappedAccounts: (AccountMigrateMap & { balance: string })[] =
    connection.bank_accounts
      ?.filter((acc) => !mappedAccounts.includes(acc.uuid))
      .map((account) => {
        const existingAccount = values.account_mappers.find(
          (a) => a?.account_id === account.uuid
        );
        return {
          account_id: account.uuid,
          action: undefined,
          closed_on: dayjs().format(YYYY_MM_DD),
          closing_balance: 0,
          balance: String(account.available_balance || 0),
          name: account.name,
          mask: account.account_number.slice(-4),
          ...existingAccount,
        };
      }) || [];

  const actionTakenOnUnmappedAccounts = unmappedAccounts.every((acc) => {
    const existingAccount = values.account_mappers.find(
      (a) => a?.account_id === acc.account_id
    );

    return (
      existingAccount &&
      (existingAccount.action === "closed" ||
        existingAccount.action === "keep_active")
    );
  });

  const reviewModal = useModal();

  const handleUnmappedAccountsConfirm = (
    accountActions: AccountMigrateMap[]
  ) => {
    const accountMap = produce(values.account_mappers, (draft) =>
      accountActions.reduce((acc, newAcc) => {
        const existingAcc = acc.findIndex(
          (a) => a?.account_id === newAcc.account_id
        );
        if (existingAcc !== -1 && acc[existingAcc]) {
          acc[existingAcc].action = newAcc.action;
          acc[existingAcc].closing_balance = newAcc.closing_balance;
          acc[existingAcc].closed_on = newAcc.closed_on;
        } else {
          acc.push({
            account_id: newAcc.account_id,
            action: newAcc.action,
            closed_on: newAcc.closed_on,
            closing_balance: newAcc.closing_balance,
            name: newAcc.name,
            mask: newAcc.mask,
          });
        }

        return acc;
      }, draft)
    );

    setFieldValue("account_mappers", accountMap);
    reviewModal.close();
  };

  const accountOptions = connection.bank_accounts?.map((bankAccount) => ({
    label: `${bankAccount.name} •••• ${bankAccount.account_number.slice(-4)}`,
    value: bankAccount.uuid,
  }));

  const filteredOptions =
    accountOptions.filter(
      (option) =>
        !values.account_mappers.some(
          (a) => !a?.action && a?.account_id === option.value
        )
    ) || [];

  const onAccountSelect = (
    value: MultiValue<OptionData> | SingleValue<OptionData>,
    index: number,
    acc: {
      name: string;
      mask: string;
    },
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (value instanceof Array) {
      return;
    }

    if (!value) {
      setFieldValue(`account_mappers[${index}]`, null);
      return;
    }

    const newAccountMapperWithoutCurrentValue = values.account_mappers.filter(
      (a) => a?.account_id !== value?.value
    );

    setFieldValue("account_mappers", newAccountMapperWithoutCurrentValue);

    setFieldValue(`account_mappers[${index}].name`, acc.name);
    setFieldValue(`account_mappers[${index}].mask`, acc.mask);

    if (value?.value === "new_account") {
      setFieldValue(`account_mappers[${index}].account_id`, undefined);
      return;
    }

    setFieldValue(`account_mappers[${index}].account_id`, value?.value);
  };

  useEffect(() => {
    intermediateBanks?.forEach((newBank, index) => {
      const similarAccount = connection.bank_accounts.find((option) => {
        const connectionBankMask = option.account_number.slice(-4);
        return connectionBankMask === newBank.mask;
      });

      if (similarAccount) {
        onAccountSelect(
          {
            label: `${
              similarAccount.name
            } •••• ${similarAccount.account_number.slice(-4)}`,
            value: similarAccount.uuid,
          },
          index,
          newBank,
          setFieldValue
        );
      }
    });
  }, [connection.bank_accounts.length, intermediateBanks?.length]);

  if (isLoading) {
    return <Loader customType="primary" addClassName="!t-h-auto" />;
  }

  const NEW_ACCOUNT_OPTION = {
    label: "Mark as new account",
    value: "new_account",
  };

  const options = [...filteredOptions, NEW_ACCOUNT_OPTION];

  return (
    <div className="t-flex t-flex-col t-gap-6">
      <p className="t-text-body-sm !t-mb-0">
        To keep your books consistent, please map each new bank account to the
        one you were previously using in Inkle. Review, adjust if needed, and
        confirm to continue.
      </p>

      <div>
        <div className="t-grid t-grid-cols-[1fr_16px_1.5fr] t-gap-4 t-items-center t-mb-1">
          <p className="t-text-body-sm t-text-text-30 t-m-0">
            Accounts connecting directly
          </p>
          <div />
          <p className="t-text-body-sm t-text-text-30 t-m-0">
            Existing accounts
          </p>
        </div>
        <div className="t-grid t-grid-cols-[1fr_16px_1.5fr] t-gap-4 t-items-center">
          {intermediateBanks?.map((acc, index) => {
            const newAccount =
              values.account_mappers[index] &&
              !values.account_mappers[index]?.account_id;

            const mappedAccount = accountOptions.find(
              (option) =>
                option.value === values.account_mappers[index]?.account_id
            );

            const defaultValue =
              mappedAccount ||
              (values.account_mappers[index] && NEW_ACCOUNT_OPTION);

            const currentMap = values.account_mappers[index];

            return (
              <>
                <span className="t-text-subtext">
                  {acc.name} •••• {acc.mask}
                </span>
                <span
                  className={classNames("-t-rotate-90 t-text-text-30", {
                    "t-mb-3": newAccount,
                  })}
                >
                  <ArrowLightLine />
                </span>
                <div
                  className={classNames({
                    "t-mb-3": newAccount,
                  })}
                >
                  <Combobox
                    menuPortalTarget={document.body}
                    onChange={(value) =>
                      onAccountSelect(value, index, acc, setFieldValue)
                    }
                    // @ts-ignore
                    defaultValue={defaultValue}
                    options={options}
                    value={accountOptions.find(
                      ({ value, label }) => value === currentMap?.account_id
                    )}
                    note={
                      <div className="t-relative">
                        <span className="t-text-caption-italic t-italic t-absolute t-top-1">
                          {newAccount
                            ? "All available transactions will be fetched for this account"
                            : ""}
                        </span>
                      </div>
                    }
                  />
                </div>
              </>
            );
          })}
        </div>
      </div>

      {Boolean(unmappedAccounts.length) && actionTakenOnUnmappedAccounts && (
        <Banner
          type="success"
          description="You can now continue with the migration."
          title={`Review completed for ${pluralize(
            unmappedAccounts.length,
            "account",
            "accounts"
          )}`}
          cta={
            <Button size="small" onClick={reviewModal.open} type="button">
              View details
            </Button>
          }
        />
      )}

      {!actionTakenOnUnmappedAccounts &&
        unmappedAccounts.length > 0 &&
        allNewAccountsMapped && (
          <Banner
            type="warning"
            description="Take necessary action on these accounts to continue with the migration."
            cta={
              <Button size="small" onClick={reviewModal.open} type="button">
                Review accounts
              </Button>
            }
            title={`${pluralize(
              unmappedAccounts.length,
              "account",
              "accounts"
            )} ${pluralize(
              unmappedAccounts.length,
              "was not",
              "were not",
              false
            )} found in the new connection`}
          />
        )}

      <ReviewAccountsModal
        isOpen={reviewModal.isOpen}
        onClose={reviewModal.close}
        unmappedAccounts={unmappedAccounts || []}
        onConfirm={handleUnmappedAccountsConfirm}
      />
    </div>
  );
};

export const ManualToPlaidToDirectMigration = ({
  connection,
  onSuccess,
}: {
  connection: BankConnection;
  onSuccess?: () => void;
}) => {
  const entityId = useCurrentEntityId();
  const entity = useCurrentEntity();
  const { uuid: groupId } = useCurrentGroupContext();
  const directMigrationAvailable = connection.direct_connection?.uuid;
  const plaidMigrationAvailable = connection.plaid_institution_id;
  const { alertToast, successToast } = useToast();

  const migrateModal = useModal();
  const [currentStep, setCurrentStep] = useState<MigrationStep>(1);
  const [isConnected, setIsConnected] = useState(false);
  const [plaidToken, setPlaidToken] = useState<string | null>(null);
  const [directCode, setDirectCode] = useState<string | null>(null);

  const { onConnectPopup } = useBankConnect({
    connectionId: directMigrationAvailable,
    onConnectSuccess: (code) => {
      if (code) {
        setDirectCode(code);
        setIsConnected(true);
      } else {
        alertToast({ message: "Failed to connect directly" });
      }
    },
  });

  const codePayload: Record<string, string | null> = {};

  if (plaidMigrationAvailable) {
    codePayload["public_token"] = plaidToken;
  }

  if (directMigrationAvailable) {
    codePayload["code"] = directCode;
  }

  const {
    data: { accounts, plaid_item_id, institution_id } = {},
    isLoading,
    isSuccess: intermediateBanksSuccess,
  } = useGetIntermediateBanksQuery(
    {
      entityId,
      entityItemId: connection.uuid,
      mapping_type: directMigrationAvailable ? "DIRECT" : "PLAID",
      connection_id: connection.direct_connection?.uuid,
      ...codePayload,
    },
    {
      skip:
        !entityId ||
        !connection.uuid ||
        (!codePayload["code"] && !codePayload["public_token"]),
    }
  );

  const [finishMigration, { isLoading: isFinishingMigration }] =
    useFinishMigrationMutation();

  const handleStepperClick = (step: number) => {
    setCurrentStep(step as MigrationStep);
  };

  const handleMigrationClick = () => {
    migrateModal.open();
    setCurrentStep(1);
    setIsConnected(false);
  };

  const handleConnectClick = async () => {
    if (directMigrationAvailable) {
      // Use direct connection
      await onConnectPopup({
        connectionId: directMigrationAvailable,
        entityId: entityId!,
      });
    } else if (plaidMigrationAvailable) {
      // Use Plaid connection - will be handled by LinkBankAccount
      setIsConnected(true);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1 && isConnected) {
      setCurrentStep(2);
    }
    if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleFinishMigration = async (values: MigrationFormValues) => {
    try {
      await finishMigration({
        entityId: entityId!,
        entityItemId: connection.uuid,
        payload: {
          code: connection.uuid,
          txn_pull_choice: values.txn_pull_choice,
          txn_pull_date: values.txn_pull_date,
          account_mappers: values.account_mappers as AccountMigrateMap[],
          new_plaid_item_id: plaid_item_id,
          institution_id: institution_id,
          mapping_type: directMigrationAvailable ? "DIRECT" : "PLAID",
          connection_id: connection.direct_connection?.uuid,
        },
      }).unwrap();

      successToast({ message: "Migration completed successfully!" });
      migrateModal.close();
      onSuccess?.();

      setCurrentStep(1);
      setIsConnected(false);
      setPlaidToken(null);
      setDirectCode(null);
    } catch (error) {
      alertToast({ message: "Failed to complete migration" });
    }
  };

  const onUnsyncComplete = useCallback((publicToken: string) => {
    setIsConnected(true);
    setPlaidToken(publicToken);
  }, []);

  const renderStepContent = (
    values: MigrationFormValues,
    setFieldValue: any
  ) => {
    switch (currentStep) {
      case 1:
        return (
          <div className="t-flex t-flex-col t-text-text-60 t-gap-4">
            {!isConnected && !intermediateBanksSuccess && (
              <>
                {directMigrationAvailable ? (
                  <>
                    <div>
                      <p className="t-text-body-sm t-mb-4">
                        Inkle now supports direct integrations with{" "}
                        <span className="t-font-bold">
                          Brex, Mercury, SVB & Rho!
                        </span>
                      </p>
                      <p className="t-text-body-sm">
                        Connecting directly provides more accurate transaction
                        data, faster, more consistent syncs, and seamless,
                        long-term reliability.
                      </p>
                    </div>

                    <p className="t-italic t-text-body-sm sm:t-text-caption-italic">
                      By continuing, you'll be redirected to your bank's login
                      to complete a secure connection. Once done, return here to
                      finish the setup.
                    </p>
                  </>
                ) : (
                  <div>
                    <p className="t-text-body-sm t-mb-4">
                      Plaid now supports connections to{" "}
                      <span className="t-font-bold">{connection.name}!</span>
                    </p>
                    <p className="t-text-body-sm">
                      Connecting via Plaid enables consistent updates, and less
                      manual effort going forward.
                    </p>
                  </div>
                )}

                <div className="t-flex t-justify-center">
                  {directMigrationAvailable ? (
                    <Button
                      onClick={handleConnectClick}
                      customType="primary"
                      type="button"
                    >
                      Connect {connection.name} directly
                    </Button>
                  ) : (
                    <MemoizedLinkBankAccount
                      sync={false}
                      onNonSyncComplete={onUnsyncComplete}
                      groupId={groupId!}
                      entityId={entityId!}
                    >
                      {({ ready, connectPlaid }) => (
                        <Button
                          type="button"
                          customType="primary"
                          disabled={!ready}
                          onClick={(e) => {
                            if (document.body) {
                              document.body.style.pointerEvents = "auto";
                            }
                            connectPlaid(e);
                          }}
                        >
                          Connect via Plaid
                        </Button>
                      )}
                    </MemoizedLinkBankAccount>
                  )}
                </div>
              </>
            )}

            {isLoading && (
              <div className="t-flex t-flex-col t-items-center t-gap-3 t-py-8">
                <span className="t-flex t-items-center t-justify-center">
                  <Loader customType="primary" addClassName="!t-h-auto" />
                </span>
                <p>Connecting to your {connection.name}...</p>
              </div>
            )}

            {intermediateBanksSuccess && (
              <div className="t-flex t-flex-col t-items-center t-gap-3 t-py-3">
                <span className="t-text-dark_green t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(112,252,144,0.15)] t-border t-border-dark_green t-border-solid t-bg-green-20">
                  <CheckCircleFilled />
                </span>
                <div className="t-flex t-flex-col t-gap-1 t-items-center t-justify-center">
                  <p className="t-text-subtext t-m-0">
                    {connection.name} connected successfully
                  </p>
                  <p className="t-text-text-30 t-m-0 t-text-body-sm">
                    Follow the next steps to finish setting up and start syncing
                    your transactions
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <MapAccounts
            connection={connection}
            intermediateBanks={accounts}
            isLoading={isLoading}
          />
        );

      case 3:
        return (
          <div className="t-flex t-flex-col t-gap-6">
            <p className="t-text-body-sm  !t-mb-0">
              Upon confirming, future transactions will start syncing via the
              new integration.
              <br />
              Now, choose if you'd like to replace your past transactions?
            </p>

            <RadioGroup.Root
              value={values.txn_pull_choice}
              onValueChange={(value) => setFieldValue("txn_pull_choice", value)}
            >
              <div className="t-flex t-flex-col t-gap-3 t-text-text-60 t-text-body-sm">
                <RadioGroup.Item value="NA">No</RadioGroup.Item>
                <RadioGroup.Item value="REPLACE_UNCATEGORIZED">
                  Yes, replace only uncategorised transactions
                </RadioGroup.Item>

                <Accordion.Root type="multiple">
                  <Accordion.Item value="advanced-options">
                    <Accordion.Content className="t-flex t-flex-col t-gap-2">
                      <RadioGroup.Item value="REPLACE_ALL">
                        Yes, replace all transactions
                      </RadioGroup.Item>
                      <RadioGroup.Item value="CUSTOM">
                        Yes, replace all transactions since a custom date
                      </RadioGroup.Item>
                      {(values.txn_pull_choice === "CUSTOM" ||
                        values.txn_pull_choice === "REPLACE_ALL") && (
                        <div className="t-mt-2">
                          <Banner
                            title="Important:"
                            type="warning"
                            description={
                              <span>
                                We recommend speaking with our support team
                                before taking this action.
                              </span>
                            }
                            cta={
                              <ConditionalLink
                                to={`/chat/${entity.bookkeeping_channel}`}
                                className="!t-no-underline"
                              >
                                <Button type="button" size="small">
                                  Go to chat
                                </Button>
                              </ConditionalLink>
                            }
                          />
                        </div>
                      )}
                    </Accordion.Content>
                    <Accordion.Trigger className="all:unset t-flex t-gap-1 t-mt-3 t-group">
                      <div className="t-flex t-gap-1 t-items-center t-text-text-30 t-text-body-sm">
                        <span>
                          <span className="group-data-state-closed:t-hidden">
                            Hide
                          </span>{" "}
                          <span className="group-data-state-open:t-hidden">
                            View
                          </span>{" "}
                          advanced options
                        </span>
                        <span className="group-data-state-open:t-rotate-180 t-transition-transform t-flex">
                          <CaretDown className="t-h-3 t-w-3" />
                        </span>
                      </div>
                    </Accordion.Trigger>
                  </Accordion.Item>
                </Accordion.Root>
              </div>
            </RadioGroup.Root>

            {values.txn_pull_choice === "CUSTOM" && (
              <Field name="txn_pull_date">
                {({ field }: FieldProps) => (
                  <DateInput
                    {...field}
                    portalId={field.name}
                    saveFormat={YYYY_MM_DD}
                    maxDate={new Date()}
                    label="Select custom date"
                  />
                )}
              </Field>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleMigrationClick();
        }}
      >
        {directMigrationAvailable ? "Connect directly" : "Connect via Plaid"}
      </Button>

      <Modal.Root
        open={migrateModal.isOpen}
        onOpenChange={() =>
          migrateModal.close({
            takeConfirmation: isConnected || intermediateBanksSuccess,
          })
        }
      >
        <Modal.Content
          useCustomOverlay
          size="large"
          onClick={stopPropagation}
          asChild
        >
          <FormikForm
            initialValues={
              {
                account_mappers: [],
                txn_pull_choice: "NA",
                txn_pull_date: "",
              } as MigrationFormValues
            }
            // @ts-ignore
            onSubmit={handleFinishMigration}
          >
            {({ values, setFieldValue, submitForm }) => {
              const mapComplete = isMapComplete(
                values as MigrationFormValues,
                connection,
                accounts || []
              );

              const replacementSelected =
                values.txn_pull_choice &&
                (values.txn_pull_choice === "CUSTOM"
                  ? Boolean(values.txn_pull_date)
                  : true);

              return (
                <>
                  <Modal.Header className="sm:t-pl-4">
                    <div>
                      <Modal.Title>
                        {directMigrationAvailable
                          ? "Connect directly"
                          : "Connect via Plaid"}
                      </Modal.Title>
                      <Modal.Subtitle>
                        <MigrationStepper
                          currentStep={currentStep}
                          onStepperClick={handleStepperClick}
                        />
                      </Modal.Subtitle>
                    </div>
                    <Modal.Close />
                  </Modal.Header>

                  <Modal.Body className="t-flex t-flex-col t-gap-6 sm:t-px-4">
                    <div className="t-flex t-flex-col t-gap-6">
                      {renderStepContent(
                        // @ts-ignore
                        values,
                        setFieldValue
                      )}
                    </div>
                  </Modal.Body>
                  {(currentStep !== 1 ||
                    isConnected ||
                    intermediateBanksSuccess) && (
                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button>Cancel</Button>
                      </Modal.RawClose>
                      {currentStep === 1 &&
                        (isConnected || intermediateBanksSuccess) && (
                          <Button
                            type="button"
                            customType="primary"
                            onClick={() => setCurrentStep(2)}
                            disabled={!intermediateBanksSuccess}
                          >
                            Next
                          </Button>
                        )}

                      {currentStep === 2 && (
                        <Button
                          type="button"
                          customType="primary"
                          onClick={handleNext}
                          disabled={!mapComplete}
                        >
                          Next
                        </Button>
                      )}

                      {currentStep === 3 && (
                        <Button
                          customType="primary"
                          onClick={submitForm}
                          disabled={
                            !mapComplete ||
                            !replacementSelected ||
                            isFinishingMigration
                          }
                          isLoading={isFinishingMigration}
                        >
                          Confirm
                        </Button>
                      )}
                    </Modal.FooterButtonGroup>
                  )}
                </>
              );
            }}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
      <TakeConfirmationModal
        isOpen={migrateModal.showConfirmation}
        dismiss={migrateModal.dismissConfirmation}
        close={migrateModal.close}
      />
    </>
  );
};
